import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { UrlParamMapping } from '../../models/url-param-mapping';
import { AlertifyService } from '../../services/alertify.service';
import {
  buildSurveyLandingUrl, buildEntryUrlParams, builtInParams, isVendorPlaceholder, landingLinkEndingClashes,
  parseQueryParams, urlParamMappingErrors
} from '../../helpers/vendor-url-params';

type RedirectLinks = {
  completeRedirectURL?: string;
  qcRedirectURL?: string;
  overQuotaRedirectURL?: string;
  terminateRedirectURL?: string;
};

let nextId = 0;

@Component({
    selector: 'or-vendor-url-params',
    templateUrl: './vendor-url-params.component.html',
    standalone: false
})
export class VendorUrlParamsComponent implements OnChanges {

  @Input() landingLinkEnding = '';
  @Input() mappings: UrlParamMapping[] = [];
  // Without a real survey link (vendor defaults in admin) the preview uses an example one.
  @Input() surveyLink?: string;
  @Input() entryUrlBase = 'https://se.navigatorsurveys.com/api/survey/incoming/{vendor-link-id}';
  @Input() redirects: RedirectLinks = {};

  @Output() landingLinkEndingChange = new EventEmitter<string>();
  @Output() mappingsChange = new EventEmitter<UrlParamMapping[]>();

  readonly idPrefix = `vendor-url-params-${nextId++}`;
  readonly exampleSurveyLink = 'https://survey.example.com/start?rid=#or1#';

  errors: (string | null)[] = [];
  suggestions: string[] = [];
  endingClashes: string[] = [];
  previewEntryUrl = '';
  previewSurveyUrl = '';
  previewSurveyParts: PreviewPart[] = [];

  constructor(private alertify: AlertifyService) { }

  // Without a real vendor link id (vendor defaults) there is no entry link worth copying.
  get canCopyEntryUrl(): boolean {
    return !this.entryUrlBase.includes('{');
  }

  ngOnChanges(): void {
    this.mappings = this.mappings ?? [];
    this.refresh();
  }

  onEndingChange(value: string): void {
    this.landingLinkEnding = value;
    this.landingLinkEndingChange.emit(value);
    this.refresh();
  }

  addMapping(vendorParam = ''): void {
    this.mappings = [...this.mappings, { vendorParam, surveyParam: vendorParam }];
    this.emitMappings();
  }

  updateMapping(index: number, field: keyof UrlParamMapping, value: string): void {
    this.mappings = this.mappings.map((mapping, i) => i === index ? { ...mapping, [field]: value.trim() } : mapping);
    this.emitMappings();
  }

  removeMapping(index: number): void {
    this.mappings = this.mappings.filter((_mapping, i) => i !== index);
    this.emitMappings();
  }

  copyEntryUrl(): void {
    navigator.clipboard?.writeText(this.previewEntryUrl).then(() => this.alertify.success('Link copied to clipboard'));
  }

  private emitMappings(): void {
    this.mappingsChange.emit(this.mappings);
    this.refresh();
  }

  private refresh(): void {
    const surveyLink = this.surveyLink || this.exampleSurveyLink;
    const entryParams = parseQueryParams(buildEntryUrlParams(surveyLink, { ...this.redirects, urlParamMappings: this.mappings }));

    // Fixed values arrive as typed; anything the vendor fills in shows as `{name}` of the
    // entry link param it came from, so `sessionId -> s2` reads `s2={sessionId}`.
    const incoming: Record<string, string> = {};
    entryParams.forEach(param => {
      incoming[param.name] = isVendorPlaceholder(param.value) ? `{${param.name}}` : param.value;
    });

    this.errors = urlParamMappingErrors(this.mappings);
    const validMappings = this.mappings.filter((_mapping, i) => !this.errors[i]);
    this.endingClashes = landingLinkEndingClashes(this.landingLinkEnding, entryParams.map(param => param.name),
      validMappings);

    const mapped = new Set(this.mappings.map(mapping => mapping.vendorParam));
    this.suggestions = entryParams.map(param => param.name).filter(name => !mapped.has(name));
    this.previewEntryUrl = this.entryUrlBase + '?' + entryParams.map(param => `${param.name}=${param.value}`).join('&');

    // surveyengine fills its own params too, so the preview shows `{or1}` rather than raw #tokens#.
    // Mapped values are url-encoded on the way in, so their braces are put back for display.
    const landingUrl = buildSurveyLandingUrl(surveyLink, incoming, validMappings, this.landingLinkEnding);
    this.previewSurveyUrl = landingUrl
      .replace(/%7B(\w+)%7D/g, '{$1}')
      .replace(/#(\w+)#/g, (token, name: string) => builtInParams.includes(name) ? `{${name}}` : token);

    const setHere = new Set([
      ...validMappings.filter(mapping => incoming[mapping.vendorParam] !== undefined).map(mapping => mapping.surveyParam),
      ...parseQueryParams(this.landingLinkEnding).map(param => param.name)
    ]);
    this.previewSurveyParts = splitPreviewUrl(this.previewSurveyUrl, setHere);
  }
}

type PreviewPart = { text: string; setHere: boolean };

// The landing link cut into text runs, marking the params this editor added or replaced.
function splitPreviewUrl(url: string, setHere: Set<string>): PreviewPart[] {
  const queryIdx = url.indexOf('?');
  if (queryIdx < 0) return [{ text: url, setHere: false }];

  const parts: PreviewPart[] = [{ text: url.substring(0, queryIdx + 1), setHere: false }];
  url.substring(queryIdx + 1).split('&').forEach((param, i) => {
    if (i > 0) parts.push({ text: '&', setHere: false });
    parts.push({ text: param, setHere: setHere.has(param.split('=')[0]) });
  });
  return parts;
}
